import { unwrap } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, StatusBadge, Tooltip, cn, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { Fragment } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { startDownload } from "../../../lib/start-download";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useAudienceLabel } from "../shared/use-shared-queries";
import type { SourceUsage, SourceView } from "../shared/types";
import { isPdf, statusVisual, typeVisual } from "./source-meta";
import { useInvalidateSource } from "./use-source-detail";
import type { SourcePermissions } from "./use-source-permissions";

interface SourceHeaderProps {
  source: SourceView;
  usage: SourceUsage | undefined;
  backHref: string;
  permissions: SourcePermissions;
  onReplace: () => void;
  onDelete: () => void;
}

function UsedBy({ usage }: { usage: SourceUsage | undefined }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  if (!usage) return null;
  if (usage.agents.length === 0) return <span>{t("deployKnowledge.sourceDetail.usedByNone")}</span>;
  return (
    <span>
      {t("deployKnowledge.sourceDetail.usedBy")}{" "}
      {usage.agents.map((agent, index) => (
        <Fragment key={agent.id}>
          {index > 0 ? (index === usage.agents.length - 1 ? ` ${t("deployKnowledge.sourceDetail.and")} ` : ", ") : null}
          <AppLink href={consoleRoutes.knowledge(summary.slug, agent.id)} className="font-semibold text-primary-ink hover:underline">
            {agent.name}
          </AppLink>
        </Fragment>
      ))}
    </span>
  );
}

function useHeaderActions(source: SourceView) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const invalidate = useInvalidateSource(source.id);
  const path = { params: { path: { source_id: source.id } } };
  const reindex = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/sources/{source_id}/reindex", path)),
    onSuccess: async (result) => {
      toast.info(t(result.crawl ? "deployKnowledge.sourceDetail.actions.recrawling" : "deployKnowledge.sourceDetail.actions.reindexing"));
      await invalidate();
    },
    onError: (error) => toast.error(message(error)),
  });
  const download = useMutation({
    mutationFn: () => unwrap(api.GET("/v1/sources/{source_id}/download", path)),
    onSuccess: (original) => {
      startDownload(original.url);
    },
    onError: (error) => toast.error(message(error)),
  });
  return { reindex, download };
}

/** Back link, type tile, title, status / access / version badges, "Used by", and actions. */
export function SourceHeader({ source, usage, backHref, permissions, onReplace, onDelete }: SourceHeaderProps) {
  const { t } = useTranslation();
  const label = useAudienceLabel();
  const visual = typeVisual(source);
  const status = statusVisual(source);
  const { reindex, download } = useHeaderActions(source);
  const statusBadge = <StatusBadge status={status.kind} icon={status.icon} label={t(`deployKnowledge.sourceDetail.${status.labelKey}`, status.values)} tabIndex={source.status_reason ? 0 : undefined} />;
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex min-w-0 items-start gap-4">
        <Button asChild variant="ghost" size="sm" iconOnly aria-label={t("deployKnowledge.sourceDetail.back")} className="mt-1 shrink-0">
          <AppLink href={backHref}>
            <Icon name="arrow-left" />
          </AppLink>
        </Button>
        <span className={cn("inline-flex size-12 shrink-0 items-center justify-center rounded-lg", visual.tile)}>
          <Icon name={visual.icon} className="size-6" />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-extrabold tracking-tight">{source.title}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            {source.status_reason ? <Tooltip content={source.status_reason}>{statusBadge}</Tooltip> : statusBadge}
            <Badge tone="brand" className="h-auto max-w-full whitespace-normal py-0.5 text-left">
              <Icon name="shield-check" className="size-3 shrink-0" />
              {source.audiences.map(label).join(", ")}
            </Badge>
            <Badge>{t("deployKnowledge.sourceDetail.version", { version: source.version })}</Badge>
            <UsedBy usage={usage} />
          </div>
          {source.status === "failed" && source.status_reason ? <p className="mt-2 text-sm text-danger">{source.status_reason}</p> : null}
          {status.kind === "warning" && source.status_reason ? <p className="mt-2 text-sm text-warning">{source.status_reason}</p> : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {permissions.replace && source.type === "file" ? (
          <Button variant="secondary" onClick={onReplace}>
            <Icon name="upload" />
            {t("deployKnowledge.sourceDetail.actions.replace")}
          </Button>
        ) : null}
        {permissions.reindex ? (
          <Button variant="secondary" loading={reindex.isPending} onClick={() => { reindex.mutate(); }}>
            <Icon name="refresh-cw" />
            {t("deployKnowledge.sourceDetail.actions.reindex")}
          </Button>
        ) : null}
        {isPdf(source) ? (
          <Button variant="secondary" iconOnly aria-label={t("deployKnowledge.sourceDetail.actions.download")} loading={download.isPending} onClick={() => { download.mutate(); }}>
            <Icon name="download" />
          </Button>
        ) : null}
        {permissions.remove ? (
          <Button variant="ghost" iconOnly aria-label={t("deployKnowledge.sourceDetail.actions.delete")} className="text-danger hover:bg-danger-soft hover:text-danger" onClick={onDelete}>
            <Icon name="trash" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
